import { CircleAlert, CircleCheck, Info, LoaderCircle, TriangleAlert, X } from 'lucide-react'
import type { ComponentProps } from 'react'
import { toast, Toaster as SonnerToaster } from 'sonner'

import { cn } from '../lib/cn'

export type ToasterProps = ComponentProps<typeof SonnerToaster>

/**
 * Região de toasts (sonner) estilizada pelos tokens. Coloque uma vez na raiz do app.
 * O sonner já anuncia por aria-live; os rótulos ficam em pt-BR. Toasts deslizam 8 px
 * (override em theme.css). Use `toast.success('Contato registrado')` repetindo o verbo da ação,
 * e `action: { label: 'Desfazer', onClick }` com `duration: 10000` para desfazer.
 */
export function Toaster({ className, toastOptions, ...props }: ToasterProps) {
  return (
    <SonnerToaster
      position="bottom-right"
      gap={8}
      offset={24}
      containerAriaLabel="Notificações"
      icons={{
        success: <CircleCheck aria-hidden className="size-[18px] text-jade" />,
        error: <CircleAlert aria-hidden className="size-[18px] text-framboesa" />,
        warning: <TriangleAlert aria-hidden className="size-[18px] text-ambar" />,
        info: <Info aria-hidden className="size-[18px] text-petroleo" />,
        loading: (
          <LoaderCircle
            aria-hidden
            className="size-[18px] animate-spin text-ink-2 motion-reduce:animate-none"
          />
        ),
        close: <X aria-hidden className="size-3.5" />,
      }}
      className={cn('font-sans', className)}
      toastOptions={{
        unstyled: true,
        closeButtonAriaLabel: 'Fechar notificação',
        ...toastOptions,
        classNames: {
          toast:
            'flex w-(--width) items-start gap-3 rounded-lg border border-border bg-surface p-3 pr-4 text-body text-ink shadow-float',
          icon: 'mt-px flex shrink-0',
          content: 'flex min-w-0 flex-1 flex-col gap-0.5',
          title: 'font-medium text-ink',
          description: 'text-compact text-ink-2',
          actionButton:
            'ml-auto inline-flex h-7 shrink-0 items-center rounded-md border border-border bg-surface px-2.5 text-compact font-medium text-ink hover:bg-surface-2',
          cancelButton:
            'inline-flex h-7 shrink-0 items-center rounded-md px-2.5 text-compact font-medium text-ink-2 hover:bg-surface-2',
          closeButton:
            'absolute -top-2 -left-2 grid size-6 place-items-center rounded-full border border-border bg-surface text-ink-2 hover:text-ink',
          ...toastOptions?.classNames,
        },
      }}
      {...props}
    />
  )
}

export { toast }
